<template>
  <div
    v-if="visible"
    class="magic-ui-checkbox-group checkbox-wrap"
    :class="{
      labelTop: config.labelStyle.location == 'top',
      labelLeft: config.labelStyle.location == 'left',
    }"

  >
    <div
      class="input-label"
      v-if="config.label.text"
      :style="{
        '--label-margin': config.labelStyle.margin,
        '--label-color': config.labelStyle.color,
        '--label-font-size': config.labelStyle.fontSize,
        '--label-font-weight': config.labelStyle.fontWeight,
        '--label-width': config.labelStyle.width,
        '--label-align': config.labelStyle.align,
      }"
    >
      <span v-if="config.verification.required" style="color: red">*</span
      ><span>{{ config.label.text }}</span>
    </div>
    <div ref="inputRef" class="input-wrap">
  <div
    :class="{ disabled: config.disabled }"
    class="input-container"
    :style="{
      '--option-margin': marginConfig.optionMargin,
      '--text-margin': marginConfig.textMargin,
    }"
  >
    <div class="option-wrap" v-for="option in options">
      <span
        v-if="config?.labelConfig.position === 'left'"
        class="radio-label"
        :style="labelStyle"
        :class="{ 'label-left': config?.labelConfig.position == 'left' }"
      >
        {{ option.label }}
      </span>
      <div
        class="radio-container"
        @click="click(option.value)"
        :class="{ checked: valueRef == option.value }"
            :style="{
        '--uncheck-color': config.iconConfig.unCheckedColor,
      }"
      >
        <input
          class="hidden-checkbox"
          type="radio"
          :value="option.value"
          :checked="option.value == valueRef"
        />
        <div class="styled-checkbox" :style="checkBoxStyle(option.value)">
          <svg class="radio-icon" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="9" :fill="config.checkedColor"></circle>
          </svg>
        </div>
      </div>
      <span
        v-if="config?.labelConfig.position != 'left'"
        class="radio-label"
        :style="labelStyle"
        :class="{ 'label-right': config?.labelConfig.position != 'left' }"
      >
        {{ option.label }}
      </span>
    </div>
  </div>
  </div>
  </div>
</template>

<script lang="ts" setup>
import { reactive, watch, inject, ref, computed, watchEffect } from 'vue';
import useApp from '@/hooks/useApp';
import useDisplay from '@/hooks/useDisplay';
import initValue from '../initValue';
import { EOptionsSource, Option, AutoOption } from '../types';
import useErrorTips from '../../../useErrorTips';

const props = defineProps<{
  config: MComponent & typeof initValue;
}>();
const app = useApp(props);
const valueRef = ref(props.config.defaultValue);
const inputRef = ref();
const errorTips = ref('');
const inputStyle = computed(() => app?.transformStyle(props.config.inputStyle));
useErrorTips(errorTips, inputRef, props.config);

const checkBoxStyle = (value: string) => {
  const size = props.config.iconConfig.size;
  const checkedColor = props.config.iconConfig.checkedColor;
  const styleObj: any = {
    height: size,
    width: size,
  };
  if (value == valueRef.value) {
    styleObj.backgroundColor = checkedColor;
  }

  return app?.transformStyle(styleObj);
};
const labelStyle = computed(() =>
  app?.transformStyle(props.config.labelConfig || {})
);

const marginConfig = computed(() =>
  app?.transformStyle(props.config.marginConfig || {})
);

watch(
  () => props.config.defaultValue,
  (val: string) => {
    valueRef.value = val;
  }
);
const setValue = (val: string) => {
  valueRef.value = val;
};

const options = computed(() => {
  let options: Array<any> = [];
  if (props.config.optionsSource === EOptionsSource.manual) {
    options = props.config?.manualOptions?.map((option: any) => {
      return {
        label: app?.runScript(option.label),
        value: app?.runScript(option.value),
      };
    });
  } else {
    options = props.config?.autoOptions;
  }
  return options || [];
});


const rules = reactive<any[]>([]);
watchEffect(() => {
  rules.splice(0, rules.length);
  const { required, customRule } = props.config.verification;
  if (required) {
    rules.push({
      validator(rule: any, value: any, callback: any) {
        if (
          valueRef.value === undefined ||
          valueRef.value === null ||
          valueRef.value === ''
        ) {
          return '必填项';
        }
      },
    });
  }
  if (customRule && typeof customRule === 'function') {
    rules.push({
      validator: (rule: any, value: any, callback: any) => {
        const result = customRule(valueRef.value);
        return result;
      },
    });
  }
});
const validate = async () => {
  for (const rule of rules) {
    const tips = await rule.validator();
    if (tips) {
      errorTips.value = tips;
      return false;
    }
  }
  errorTips.value = '';
  return true;
};
const clearValidate = () => {
  errorTips.value = '';
};

const click = (value: any) => {
  if(props.config.disabled) return;
  if (valueRef.value === value) {
    valueRef.value = '';
  } else {
    valueRef.value = value;
  }
  validate();
};

const useFormField = inject<any>('useFormField', undefined);
if (useFormField) {
  useFormField({
    fieldPropsConfig: props.config,
    fieldValueRef: valueRef,
    validate,
    clearValidate,
  });
}

const { visible, show, hide } = useDisplay(props);
// 暴露给编辑器的接口请注册在这里
const exportInterface = reactive({
  errorMessage: errorTips,  
  value: valueRef,
  setValue,
  show,
  hide,
});

defineExpose(exportInterface);
</script>
<style lang="scss" scoped>
.disabled {
  opacity: 0.5;
}
.checkbox-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  .input-label {
    flex-shrink: 0;
    font-size: var(--label-font-size);
    color: var(--label-color);
    font-weight: var(--label-font-weight);
    min-width: var(--label-width);
    justify-content: var(--label-align);
    display: flex;    
  }
  &.labelTop {
    display: inline-block;
    .input-label {
      margin-bottom: var(--label-margin);
    }
  }
  &.labelLeft {
    display: flex;
    .input-label {
      margin-right: var(--label-margin);
    }
  }
  align-items: center;
  .input-wrap {
    flex: 1;
    .input-container {
      display: flex;
    }
  }
}
.option-wrap {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  margin-right: var(--option-margin);
  &.disabled {
    opacity: 0.6;
  }
  .label-left {
    margin-right: var(--text-margin);
  }
  .label-right {
    margin-left: var(--text-margin);
  }
  .radio-container {
    display: inline-block;
    vertical-align: middle;
    .hidden-checkbox {
      border: 0;
      /* clip: rect(0 0 0 0); */
      clippath: inset(50%);
      height: 1px;
      margin: -1px;
      overflow: hidden;
      padding: 0;
      position: absolute;
      white-space: nowrap;
      width: 1px;
    }
    .styled-checkbox {
      border: 1px solid rgba(5, 5, 5, 0.26);
      border-radius: 100%;
      transition: all 150ms;
      background: var(--uncheck-color);  
      display: inline-block;
      .hidden-checkbox:focus + & {
        box-shadow: 0 0 0 3px rgba(0, 0, 0, 0);
      }
    }
    .radio-icon {
      fill: none;
      stroke: white;
      stroke-width: 2px;
      visibility: hidden;
    }
    &.checked {
      .radio-icon {
        visibility: visible;
      }
    }
  }
}
.errorTip {
  border-color: 1px solid red;
}
.error-tips {
  color: red;
  margin-top: 2px;
  font-size: 12px;
}
</style>
